import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { CBadge, CButton, CButtonGroup, CCard } from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilCheckAlt, cilChevronRight, cilInfo } from "@coreui/icons";
import { useTheme } from "@scryerlabs/ui";
import { DrawdownZone } from "../portfolio-tracker/components/chart-parts";
import { FallStrip } from "../portfolio-tracker/components/FallStrip";
import { StackedArea } from "../portfolio-tracker/components/StackedArea";
import {
  chartFrame,
  formatDay,
  formatDayRange,
  stackProfits,
  strategyColours,
  type DatedValue,
} from "../portfolio-tracker/portfolio-tracker";
import { useElementSize } from "../portfolio-tracker/useElementSize";
import { Key } from "./HelpExampleKey";

/**
 * A worked example of the portfolio tracker's results, and one strategy's trades, keyed 1-12 so the article can
 * name each part.
 *
 * Built from literals, as the other examples are: the real page needs the customer's own signals and a saved
 * setup. The figures are one portfolio worked out by the tracker's rules, so they add up the way the article
 * says: $100,000 split 40/30/30 from Jan 5, 2026, one shared balance, no buy cut back. The chart is drawn by
 * the page's own chart parts from the same figures.
 */

const MINUS = "−";
const START = "2026-01-05";
const AS_OF = "2026-11-02";
const PUT_IN = 100_000;

/** The balance at the start and after each exit. */
const BALANCE: DatedValue[] = [
  { date: START, value: 100_000 },
  { date: "2026-02-09", value: 104_000 },
  { date: "2026-02-23", value: 107_000 },
  { date: "2026-03-23", value: 102_720 },
  { date: "2026-04-13", value: 105_720 },
  { date: "2026-05-18", value: 102_548.4 },
  { date: "2026-06-22", value: 101_010.17 },
  { date: "2026-07-06", value: 109_467.77 },
  { date: "2026-08-24", value: 112_751.81 },
  { date: "2026-09-14", value: 114_941.16 },
  { date: AS_OF, value: 114_941.16 },
];

/** Each strategy's profit so far, on the balance's days. */
const PROFITS = [
  { id: "A", values: [0, 4_000, 4_000, -280, -280, -280, -280, 8_177.6, 8_177.6, 10_366.96, 10_366.96] },
  { id: "B", values: [0, 0, 0, 0, 3_000, 3_000, 1_461.77, 1_461.77, 1_461.77, 1_461.77, 1_461.77] },
  { id: "C", values: [0, 0, 3_000, 3_000, 3_000, -171.6, -171.6, -171.6, 3_112.43, 3_112.43, 3_112.43] },
];

/** How far below its highest the balance was, on the same days. */
const FALL: DatedValue[] = [0, 0, 0, -4, -1.196, -4.16, -5.598, 0, 0, 0, 0].map((value, index) => ({
  date: BALANCE[index].date,
  value,
}));

const DRAWDOWN = { percent: 5.598, from: "2026-02-23", to: "2026-06-22", backOn: "2026-07-06" };

const STRATEGIES = [
  { id: "A", ticker: "TQQQ", name: "Example Strategy A", side: 1, through: "TQQQ", allocation: "40%", won: 3, lost: 1, open: 0, unpriced: 0, profit: "+$10,367", width: 50, share: "69.4%" },
  { id: "B", ticker: "ETHUSD", name: "Example Strategy B", side: 1, through: "ETHUSDT", allocation: "30%", won: 1, lost: 1, open: 1, unpriced: 0, profit: "+$1,462", width: 7.05, share: "9.8%" },
  { id: "C", ticker: "FNGD", name: "Example Strategy C", side: 3, through: "FNGD and FNGU", allocation: "30%", won: 2, lost: 1, open: 0, unpriced: 1, profit: "+$3,112", width: 15.01, share: "20.8%" },
];

/** Profit by strategy ranks them by profit. */
const RANKED = ["A", "C", "B"];

export function HelpExamplePortfolioTracker() {
  const { t } = useTranslation(["portfolioTracker", "tradingStrategies"]);
  const { theme } = useTheme();
  const hostRef = useRef<HTMLDivElement>(null);
  const size = useElementSize(hostRef);

  const colours = useMemo(() => {
    const ramp = strategyColours(STRATEGIES.length, theme);
    return new Map(STRATEGIES.map((strategy, index) => [strategy.id, ramp[index]]));
  }, [theme]);
  const stack = useMemo(
    () =>
      stackProfits(
        BALANCE.map((point) => point.date),
        PUT_IN,
        BALANCE,
        PROFITS,
      ),
    [],
  );
  const width = Math.max(260, size.width);
  const height = Math.max(120, size.height);
  const frame = chartFrame(width, height, 0, stack.top * 1.06, START, AS_OF);

  const ident = (strategy: (typeof STRATEGIES)[number]) => (
    <span className="tracker-ident">
      <span className="tracker-ident-top">
        <span className="tracker-ticker">{strategy.ticker}</span>
        <span className="tracker-ident-name">{strategy.name}</span>
      </span>
      <span className="tracker-ident-sub">
        <span>{t(`tradingStrategies:sides.${strategy.side}`)}</span>
        <span aria-hidden="true">·</span>
        <span>{strategy.through}</span>
        <span className="tracker-timeframe">1D</span>
      </span>
    </span>
  );

  const tally = (strategy: (typeof STRATEGIES)[number]) =>
    [
      t("tally.wonLost", { won: strategy.won, lost: strategy.lost }),
      strategy.open ? t("tally.open", { count: strategy.open }) : null,
      strategy.unpriced ? t("tally.unpriced", { count: strategy.unpriced }) : null,
    ]
      .filter(Boolean)
      .join(" · ");

  const tiles: [string, string, string, string, number][] = [
    [t("tiles.putIn"), "$100,000", t("tiles.strategies", { count: 3 }), "", 1],
    [t("tiles.worth"), "$114,941", t("tiles.on", { date: formatDay(AS_OF) }), "", 2],
    [t("tiles.profit"), "+$14,941", "+14.9%", "text-success", 3],
    [
      t("tiles.drawdown"),
      `${MINUS}5.6%`,
      t("tiles.fellBack", { range: formatDayRange(DRAWDOWN.from, DRAWDOWN.to), back: formatDay(DRAWDOWN.backOn) }),
      "text-danger",
      4,
    ],
    [
      t("tiles.trades"),
      t("tally.closed", { count: 9 }),
      `${t("tally.wonLost", { won: 6, lost: 3 })} · ${t("tally.open", { count: 1 })}`,
      "",
      5,
    ],
  ];

  return (
    <figure className="help-example-card help-example-page help-example-tracker">
      <div className="portfolio-tracker">
        <div className="tracker-results">
          <CCard className="tracker-summary">
            <div className="tracker-summary-top">
              <div>
                <div className="tracker-eyebrow">{t("results.eyebrow")}</div>
                <div className="h5 mb-0">{t("results.period", { from: formatDay(START), to: formatDay(AS_OF) })}</div>
                <p className="tracker-saved">
                  <CIcon icon={cilCheckAlt} size="sm" />
                  {t("results.saved")}
                </p>
              </div>
            </div>
            <div className="tracker-tiles">
              {tiles.map(([label, value, sub, tone, key]) => (
                <div key={label} className="tracker-tile">
                  <div className="tracker-eyebrow">
                    {label}
                    <Key n={key} />
                  </div>
                  <div className={`tracker-tile-value ${tone}`}>{value}</div>
                  <div className={`tracker-tile-sub ${tone}`}>{sub}</div>
                </div>
              ))}
            </div>
            <ul className="tracker-notes">
              <li>
                <CIcon icon={cilInfo} size="sm" />
                <span>{t("results.unpriced", { count: 1 })}</span>
              </li>
              <li>
                <CIcon icon={cilInfo} size="sm" />
                <span>{t("results.alertPriced", { count: 3 })}</span>
              </li>
            </ul>
          </CCard>

          <div className="tracker-charts">
            <CCard className="tracker-chart-card">
              <div className="tracker-chart-head">
                <div className="h6 mb-0">{t("chart.title")}</div>
                <CButtonGroup size="sm" className="btn-group-pill">
                  <CButton color="primary" variant="outline" tabIndex={-1}>
                    {t("chart.total")}
                  </CButton>
                  <CButton color="primary" tabIndex={-1}>
                    {t("chart.strategy")}
                  </CButton>
                </CButtonGroup>
              </div>
              <div className="tracker-legend">
                <span>
                  <i className="tracker-key-base-band" />
                  {t("chart.keyPutIn")}
                </span>
                <span>
                  <i className="tracker-key-bands" />
                  {t("chart.keyBands")}
                  <Key n={6} />
                </span>
                <span>
                  <i className="tracker-key-total" />
                  {t("chart.keyPortfolio")}
                </span>
                <span>
                  <i className="tracker-key-fall" />
                  {t("chart.keyFall")}
                  <Key n={7} />
                </span>
              </div>
              <div className="tracker-chart-wrap help-example-chart" aria-hidden>
                <div ref={hostRef} className="tracker-chart">
                  {size.width > 0 && (
                    <svg viewBox={`0 0 ${width} ${height}`}>
                      <StackedArea
                        frame={frame}
                        putIn={PUT_IN}
                        balance={BALANCE}
                        stack={stack}
                        colours={colours}
                        zone={
                          <DrawdownZone
                            frame={frame}
                            from={DRAWDOWN.from}
                            to={DRAWDOWN.to}
                            percent={DRAWDOWN.percent}
                          />
                        }
                        hoverDay={null}
                        hoverValue={0}
                      />
                    </svg>
                  )}
                </div>
                {size.width > 0 && <FallStrip frame={frame} fall={FALL} percent={DRAWDOWN.percent} hoverDay={null} />}
              </div>
            </CCard>

            <CCard className="tracker-chart-card tracker-side">
              <div className="h6 mb-0">
                {t("results.byStrategy")}
                <Key n={8} />
              </div>
              <p className="tracker-side-when">
                {t("results.onDay", { date: formatDay(AS_OF), worth: "$114,941", putIn: "$100,000" })}
              </p>
              <p className="tracker-takeaway">
                {t("results.takeaway.most", { names: "Example Strategy A", percent: "69.4%" })}
              </p>
              <div className="tracker-bars">
                {RANKED.map((id) => {
                  const strategy = STRATEGIES.find((candidate) => candidate.id === id)!;
                  return (
                    <div key={id} className="tracker-bar-row">
                      <span className="tracker-bar-name">
                        <i className="tracker-swatch" style={{ background: colours.get(id) }} />
                        <span className="tracker-bar-text">
                          <span className="tracker-bar-title">{strategy.name}</span>
                          <small>{t("results.shareOfGains", { percent: strategy.share })}</small>
                        </span>
                      </span>
                      <span className="tracker-track">
                        <i className="is-up" style={{ width: `${strategy.width}%` }} />
                      </span>
                      <span className="tracker-bar-value text-success">{strategy.profit}</span>
                    </div>
                  );
                })}
              </div>
              <p className="tracker-chart-note mb-0">{t("results.barsNote")}</p>
            </CCard>
          </div>

          <CCard className="tracker-table">
            <div className="tracker-table-title">
              <div className="h6 mb-0">
                {t("table.title")}
                <Key n={9} />
              </div>
              <p className="mb-0">{t("table.description")}</p>
            </div>
            <div className="tracker-table-head">
              <span>{t("table.strategy")}</span>
              <span>{t("table.allocation")}</span>
              <span>{t("table.from")}</span>
              <span>{t("table.trades")}</span>
              <span className="text-end">{t("table.profit")}</span>
              <span className="text-end">{t("table.share")}</span>
              <span />
            </div>
            {STRATEGIES.map((strategy) => (
              <div key={strategy.id} className="tracker-table-row">
                <span className="tracker-cell-strategy">{ident(strategy)}</span>
                <span className="tracker-cell" data-label={t("table.allocation")}>
                  <b>{strategy.allocation}</b>
                  <small>{t("table.ofBalance")}</small>
                </span>
                <span className="tracker-cell" data-label={t("table.from")}>
                  <b>{formatDay(START)}</b>
                </span>
                <span className="tracker-cell" data-label={t("table.trades")}>
                  <b>{t("tally.closed", { count: strategy.won + strategy.lost })}</b>
                  <small>{tally(strategy)}</small>
                </span>
                <span className="tracker-cell is-number text-success" data-label={t("table.profit")}>
                  <b>{strategy.profit}</b>
                </span>
                <span className="tracker-cell is-number" data-label={t("table.share")}>
                  <b>{strategy.share}</b>
                </span>
                <span className="tracker-open">
                  {t("table.open")}
                  <CIcon icon={cilChevronRight} size="sm" />
                </span>
              </div>
            ))}
            <div className="tracker-table-row is-total">
              <span className="tracker-cell-strategy">
                <b>{t("table.total")}</b>
              </span>
              <span className="tracker-cell" data-label={t("table.allocation")}>
                <b>100%</b>
                <small>{t("table.ofBalance")}</small>
              </span>
              <span className="tracker-cell is-gap" />
              <span className="tracker-cell" data-label={t("table.trades")}>
                <b>{t("tally.closed", { count: 9 })}</b>
                <small>{t("tally.wonLost", { won: 6, lost: 3 })}</small>
              </span>
              <span className="tracker-cell is-number text-success" data-label={t("table.profit")}>
                <b>+$14,941</b>
                <small>+14.9%</small>
              </span>
              <span className="tracker-cell is-number" data-label={t("table.share")}>
                <b>100%</b>
              </span>
              <span className="is-gap" />
            </div>
          </CCard>

          <CCard className="tracker-trades">
            <div className="tracker-trades-head">
              <div className="tracker-eyebrow">Example Strategy C · its own page</div>
              <div className="h6 mb-0">{t("trades.title")}</div>
              <p className="mb-0">{t("trades.description", { percent: "30%" })}</p>
            </div>
            <div className="tracker-trades-scroll">
              <table className="tracker-trades-table">
                <thead>
                  <tr>
                    <th>{t("trades.bought")}</th>
                    <th className="text-end">{t("trades.price")}</th>
                    <th className="text-end">
                      {t("trades.amount")}
                      <Key n={10} />
                    </th>
                    <th>{t("trades.exited")}</th>
                    <th className="text-end">{t("trades.price")}</th>
                    <th className="text-end">{t("trades.held")}</th>
                    <th className="text-end">{t("trades.return")}</th>
                    <th className="text-end">{t("trades.profit")}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <span className="tracker-ticker is-small">FNGD</span>
                      {formatDay("2026-07-13")}
                    </td>
                    <td className="text-end">$13.00</td>
                    <td className="text-end">
                      $32,840
                      <small>{t("trades.ofBalance", { percent: "30%", balance: "$109,468" })}</small>
                    </td>
                    <td>{formatDay("2026-08-24")}</td>
                    <td className="text-end">$14.30</td>
                    <td className="text-end">{t("trades.days", { count: 42 })}</td>
                    <td className="text-end text-success">+10.0%</td>
                    <td className="text-end text-success">+$3,284</td>
                  </tr>
                  <tr>
                    <td>
                      <span className="tracker-ticker is-small">FNGD</span>
                      {formatDay("2026-04-20")}
                    </td>
                    <td className="text-end">$14.00</td>
                    <td className="text-end">
                      $31,716
                      <small>{t("trades.ofBalance", { percent: "30%", balance: "$105,720" })}</small>
                    </td>
                    <td>{formatDay("2026-05-18")}</td>
                    <td className="text-end">$12.60</td>
                    <td className="text-end">{t("trades.days", { count: 28 })}</td>
                    <td className="text-end text-danger">{MINUS}10.0%</td>
                    <td className="text-end text-danger">{MINUS}$3,172</td>
                  </tr>
                  <tr>
                    <td>
                      <span className="tracker-ticker is-small">FNGU</span>
                      <span className="tracker-short-side">{t("trades.shortSide")}</span>
                      {formatDay("2026-03-09")}
                    </td>
                    <td className="text-end">
                      <span className="text-body-secondary">—</span>
                      <small>
                        {t("trades.noPrice")}
                        <Key n={12} />
                      </small>
                    </td>
                    <td className="text-end">
                      <span className="text-body-secondary">—</span>
                    </td>
                    <td>{formatDay("2026-03-30")}</td>
                    <td className="text-end">
                      <span className="text-body-secondary">—</span>
                      <small>{t("trades.noPrice")}</small>
                    </td>
                    <td className="text-end">{t("trades.days", { count: 21 })}</td>
                    <td className="text-end" colSpan={2}>
                      <CBadge color="secondary" shape="rounded-pill">
                        {t("trades.noPrice")}
                      </CBadge>
                      <small>{t("trades.leftOut")}</small>
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <span className="tracker-ticker is-small">FNGD</span>
                      {formatDay("2026-01-26")}
                    </td>
                    <td className="text-end">
                      $12.00
                      <small>
                        {t("trades.alertPrice")}
                        <Key n={11} />
                      </small>
                    </td>
                    <td className="text-end">
                      $30,000
                      <small>{t("trades.ofBalance", { percent: "30%", balance: "$100,000" })}</small>
                    </td>
                    <td>{formatDay("2026-02-23")}</td>
                    <td className="text-end">
                      $13.20
                      <small>{t("trades.alertPrice")}</small>
                    </td>
                    <td className="text-end">{t("trades.days", { count: 28 })}</td>
                    <td className="text-end text-success">+10.0%</td>
                    <td className="text-end text-success">+$3,000</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CCard>
        </div>
      </div>

      <figcaption>
        An example. <strong>Not real strategies</strong> — the names and figures are invented.
      </figcaption>
    </figure>
  );
}
